/**
 * Reading a customer's item list out of an UNSTRUCTURED mail body, using the configured LLM.
 *
 * ---- Why this exists alongside `readMailItems.ts` ----
 *
 * `readMailItems` handles the two shapes that carry their own structure: an HTML table (10 of 25
 * inbound mails on this bench) and a numbered text list with a unit beside each quantity. Those
 * are parsed deterministically, for nothing, and this module must never displace them — a grid
 * with column headings is stronger evidence than any model's reading of it.
 *
 * What is left is prose. A vessel writes "we need a couple of drums of hydraulic oil and 4 boxes
 * of gloves before Thursday" and there is no grid, no numbering, and no fixed word order to key
 * on. Every extra heuristic rule for that fails on the next customer's phrasing. So: the model
 * reads it, and a human checks the result before an enquiry exists.
 *
 * ---- Two rules ----
 *
 * **The token never reaches the browser.** The request goes to a same-origin `/llm` path that the
 * dev server (`vite.config.ts`) and the production server (`server/index.ts`) proxy upstream with
 * the bearer attached. Identical to how `bosun-search` is reached, for the identical reason.
 *
 * **Nothing it returns is applied unattended.** The caller shows an editable review grid and the
 * purchaser confirms. A quantity the model could not read arrives as `0`, never as a guess — a
 * vessel asking for 200 must not receive 1 because a parser preferred a tidy answer.
 */

import { currentClerkToken } from './api/frappeSession'
import type { SheetItem } from './sheetExtract'
import type { ExtractedLine } from './api/mailExtractionState'

/** Where the proxy lives. Same-origin, so no credential is ever in client code. */
const LLM_PATH = '/llm'

/**
 * Which model reads the mail.
 *
 * Public on purpose — a model NAME is not a credential, unlike the token, which never leaves the
 * proxy. Supplied at build time from `BOSUN_LLM_MODEL` (see `vite.config.ts`'s `define`) so dev
 * and production read the same `.env` entry the proxy target comes from.
 */
const LLM_MODEL: string =
  (import.meta.env?.VITE_BOSUN_LLM_MODEL as string | undefined) ?? 'deepseek/deepseek-v3.2-exp'

/**
 * A mail body can mention hundreds of things in passing. This bounds what one bad answer can
 * turn into — a review grid of 200 rows is already unusable, and 500 is a denial of service on
 * the purchaser's attention.
 */
const MAX_LINES = 200

/**
 * Deliberately narrow. It asks for what the enquiry needs and forbids the two failure modes that
 * matter: inventing a quantity, and returning items the mail does not actually order (a signature
 * block, a vessel's particulars, a previous order quoted underneath the reply).
 */
export const EXTRACTION_PROMPT = [
  'You read ship-chandler purchase enquiries.',
  'From the email below, list ONLY the items the customer is asking to buy in THIS message.',
  'Ignore signatures, disclaimers, vessel particulars, and any quoted or forwarded earlier email.',
  'Reply with JSON only, no prose and no code fence, in exactly this shape:',
  '{"items":[{"description":"...","qty":0,"uom":"..."}]}',
  'Rules:',
  '- description: the item as the customer wrote it, verbatim. Do not correct or expand it.',
  '- qty: the number they asked for. If the email does not state one, use 0. NEVER guess.',
  '- uom: the unit as written (pcs, box, drum, ltr...). If none is stated, use "".',
  '- Include an item even when no quantity is given (use 0). Asking for a price IS a request.',
  '- If the email orders nothing, reply {"items":[]}.',
].join('\n')

const str = (v: unknown): string => (typeof v === 'string' ? v.trim() : '')

/**
 * A quantity, or 0 — never a guess.
 *
 * Accepts a numeric string because models emit `"12"` as often as `12`. Anything that is not a
 * finite non-negative number ("about ten", null, "a few") becomes 0, which the review grid shows
 * as blank for the purchaser to fill in.
 */
function qtyOf(v: unknown): number {
  const n = typeof v === 'number' ? v : typeof v === 'string' ? Number(v.trim()) : NaN
  return Number.isFinite(n) && n >= 0 ? n : 0
}

/**
 * The model's answer → item lines, or `null` when it did not answer with JSON at all.
 *
 * `null` and `[]` are different and both are real: `[]` means "this mail orders nothing", which
 * the screen may state; `null` means "the model did not give us something we can read", which is
 * a failure and must be reported as one rather than shown as an empty order.
 */
export function parseExtractedLines(answer: string): SheetItem[] | null {
  const text = answer.trim()
  if (text === '') return null

  // Models wrap JSON in ```json fences constantly. Refusing that would reject right answers.
  const fenced = text.match(/```(?:json)?\s*([\s\S]*?)```/i)
  const body = (fenced ? fenced[1] : text).trim()

  let payload: unknown
  try {
    payload = JSON.parse(body)
  } catch {
    return null
  }

  const rows = Array.isArray(payload)
    ? payload
    : Array.isArray((payload as { items?: unknown })?.items)
      ? (payload as { items: unknown[] }).items
      : null
  if (!rows) return null

  const out: SheetItem[] = []
  for (const row of rows) {
    if (typeof row !== 'object' || row === null) continue
    const r = row as Record<string, unknown>
    const description = str(r.description) || str(r.item) || str(r.name)
    // A row with no description is not a line — it cannot be matched, priced, or queried.
    if (description === '') continue
    out.push({
      description,
      qty: qtyOf(r.qty ?? r.quantity),
      uom: str(r.uom) || str(r.unit),
      // Prose carries neither. NEVER invented: `item_code` is a Link the server validates, and a
      // customer's line number is echoed to the quotation — inventing either is worse than a gap.
      item_code: null,
      row_no: null,
    })
    if (out.length >= MAX_LINES) break
  }
  return out
}

/**
 * `Authorization: Bearer <clerk jwt>` for `/llm`, or nothing.
 *
 * The deployed `/llm` route is Clerk-gated exactly like `/search` (`server/index.ts`), so a request
 * without this header is refused by OUR server with a 401 before OpenRouter is ever contacted —
 * which reads as "the model is failing" when nothing is wrong with the model or the key at all.
 *
 * `currentClerkToken` rather than `proxyBearer`, for the same reason `useItems.ts`'s search calls
 * use it: `/llm` is a bare same-origin path that always reaches our own server, whatever
 * `apiBase()` points at. `null` in dev, where `vite.config.ts` proxies `/llm` with the token
 * injected server-side and performs no Clerk check.
 */
async function llmAuthHeaders(): Promise<Record<string, string>> {
  const bearer = await currentClerkToken()
  return bearer ? { Authorization: `Bearer ${bearer}` } : {}
}

/** Whether this deployment can read a mail body at all — the `/llm` proxy is optional. */
export async function mailReaderAvailable(): Promise<boolean> {
  try {
    // A HEAD is enough: the route exists (any status) or it does not (network error / 404 shell).
    const res = await fetch(LLM_PATH, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(await llmAuthHeaders()) },
      body: '{}',
    })
    return res.status !== 404
  } catch {
    return false
  }
}

/**
 * Ask the model to read one mail body.
 *
 * Throws on transport or shape failure so the caller can SAY so — this path is entered by a
 * deliberate click on a mail that could not be read otherwise, and silently returning nothing
 * would be indistinguishable from the failure it is meant to rescue.
 */
export async function extractLinesFromBody(bodyText: string, model: string = LLM_MODEL): Promise<SheetItem[]> {
  const res = await fetch(LLM_PATH, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(await llmAuthHeaders()) },
    body: JSON.stringify({
      model,
      // Deterministic: the same mail read twice must give the same lines, or a purchaser cannot
      // trust a re-read after a correction.
      temperature: 0,
      messages: [
        { role: 'system', content: EXTRACTION_PROMPT },
        { role: 'user', content: bodyText },
      ],
    }),
  })
  if (!res.ok) throw new Error(`The mail reader answered ${res.status}.`)

  const json = (await res.json()) as { choices?: Array<{ message?: { content?: unknown } }> }
  const answer = str(json.choices?.[0]?.message?.content)
  const lines = parseExtractedLines(answer)
  if (lines === null) throw new Error('The mail reader did not return a readable list.')
  return lines
}


/**
 * Reviewed body lines → the shape the create path already takes.
 *
 * The UOM is the interesting part. A mail body says "boxes", "drums", "pails" — words, not `UOM`
 * master docnames. `Enquiry Item.uom` is a Frappe Link, so sending one fails the WHOLE document
 * (`Could not find Row #1: UOM: NO`). That is exactly the case `verifyUoms` already produces for
 * the spreadsheet path, and `toCreateLine` already handles it: `uom: null` with the original on
 * `raw_uom` becomes the placeholder unit plus "(customer's unit: boxes)" on the text, so nothing
 * the customer wrote is lost and the purchaser resolves it during triage.
 *
 * So this deliberately does NOT try to resolve units — it hands them over unverified, by the same
 * route, rather than inventing a second convention.
 *
 * `confidence: 0` because prose is the least reliable source there is, and the CONF. column should
 * say so until the matcher or a human replaces it.
 */
export function toExtractedLine(item: SheetItem): ExtractedLine {
  const raw = (item.uom ?? '').trim()
  return {
    item_code: null,
    description: item.description,
    qty: item.qty,
    // Never verified here — see the header.
    uom: null,
    raw_uom: raw === '' ? undefined : raw,
    confidence: 0,
  }
}
